import type { StockDetail, StockListItem } from './types'

// ── K线生成工具（30根日K）────────────────────────────────────
function genKlines(base: number, count = 30, startDate = '2025-05-01') {
    const result = []
    let price = base
    const d = new Date(startDate)
    for (let i = 0; i < count; i++) {
        const chg = (Math.random() - 0.48) * base * 0.025
        const open = price
        const close = +(price + chg).toFixed(2)
        const high = +(Math.max(open, close) + Math.random() * base * 0.01).toFixed(2)
        const low = +(Math.min(open, close) - Math.random() * base * 0.01).toFixed(2)
        const volume = Math.floor(1e7 + Math.random() * 5e7)
        result.push({ ts: d.toISOString().slice(0, 10), open, high, low, close, volume })
        price = close
        d.setDate(d.getDate() + 1)
        if (d.getDay() === 0) d.setDate(d.getDate() + 1)
        if (d.getDay() === 6) d.setDate(d.getDate() + 2)
    }
    return result
}

// ── 腾讯 00700.HK ────────────────────────────────────────────
const tencent: StockDetail = {
    code: 'HK.00700', name: '腾讯控股', sector: '科技', market: 'hk', currency: 'HKD',
    listing_date: '2004-06-16', total_shares: 9540000000,
    klines: genKlines(390, 30, '2025-05-01'),
    reports: [
        { id: 1, title: '腾讯2024年报深度解析：游戏+广告双轮驱动，AI布局初现成效', source: '高盛', publish_date: '2025-03-28', summary: '腾讯2024年营收同比增长8%至6601亿元，广告收入强劲增长17%，微信视频号商业化进入加速期。国际游戏业务恢复增长，PUBG Mobile及Supercell旗下产品表现优异。AI Coding工具（Copilot）日活超百万，云业务毛利率提升至30%。维持买入评级，目标价460港元。', tags: ['业绩', '广告', '游戏', 'AI'] },
        { id: 2, title: '微信生态商业化新阶段：视频号与小程序协同效应分析', source: '摩根大通', publish_date: '2025-01-15', summary: '视频号广告收入2024年预计突破150亿元，小程序交易额超过2万亿。腾讯微信生态形成"内容-社交-支付-商业"闭环，广告ARPU持续提升。我们认为微信商业化仍处早期，未来3年广告收入复合增长率可达15%+。', tags: ['微信', '视频号', '广告'] },
    ],
    indicators: [
        {
            id: 1, report_date: '2024-03-20', fiscal_year: '2023', report_type: 'annual',
            closing_date: '2024-03-20', closing_price: 312.4, currency: 'HKD',
            revenue: 609015000000, revenue_yoy_pct: 10.0, gross_profit: 310000000000, gross_margin_pct: 50.9,
            net_profit_parent: 157688000000, adjusted_net_profit_yoy_pct: 36.0,
            net_profit_ex_non_recurring: 148000000000, net_profit_ex_non_recurring_yoy_pct: 34.0,
            net_margin_pct: 25.9, period_expense_ratio_pct: 18.2,
            selling_distribution_expense: 28000000000, admin_expense: 35000000000, finance_cost: 3500000000,
            profit_before_tax: 165000000000, income_tax: 18000000000, ebitda: 200000000000,
            total_assets: 1180000000000, total_liabilities: 540000000000, net_assets_equity: 550000000000,
            debt_to_asset_ratio_pct: 45.8, cash_and_equivalents: 361745000000, interest_bearing_debt: 180000000000,
            current_assets: 520000000000, current_liabilities: 290000000000, current_ratio: 1.79,
            total_equity: 640000000000, parent_equity: 550000000000,
            operating_cash_flow_cfo: 196000000000, investing_cash_flow_cfi: -85000000000,
            financing_cash_flow_cff: -120000000000, free_cash_flow_fcf: 168000000000,
            roe_pct: 28.7, roa_pct: 13.4, eps_basic: 16.89, eps_diluted: 16.45,
            total_dividend: 32000000000, dividend_payout_ratio_pct: 20.3, dividend_per_share: 3.4,
            dividend_yield_pct: 1.09, share_buyback_amount: 49200000000, ex_dividend_date: '2024-05-15',
            pe_ratio: 18.5, pe_ttm: 17.2, pb_ratio: 2.8, ps_ratio: 2.9, ev_ebitda: 12.4, market_cap: 2980000000000,
            revenue_sources: '增值服务(54%)、网络广告(25%)、金融科技及企业服务(21%)',
            revenue_growth_drivers: '视频号广告商业化加速、国际游戏业务复苏、企业微信SaaS增长',
            annual_report_major_events: '完成对Ubisoft战略投资；发布混元大模型；微信视频号广告收入首次超百亿',
            indicators_step_1_md: `## 财务健康度评估

**盈利能力：优秀**
- 毛利率50.9%，体现平台型商业模式的高壁垒
- 调整后净利润同比增长36%，显著超预期
- 自由现金流168亿元，现金创造能力极强

**资产质量：良好**
- 货币资金3618亿元，净现金状态稳健
- 流动比率1.79，短期偿债无压力
- 资产负债率45.8%，处于合理区间`,
            indicators_step_2_md: `## 投资价值综合判断

**核心逻辑**：腾讯正从高速增长转向高质量增长阶段。广告业务受益于视频号商业化，游戏业务国际化打开第二增长曲线，AI基础设施投入将在2025-2026年开始贡献商业价值。

**风险提示**：监管政策不确定性、游戏版号审批节奏、地缘政治对海外业务影响。

**估值判断**：当前PE(TTM)约17x，处历史中低位，考虑到增长质量改善，具备较好安全边际。`,
            kpi_metrics: { '微信月活': '13.43亿', '小程序交易额': '超2万亿元', '视频号日活': '5亿+', '企业微信企业数': '1000万+' },
            kpi_guidance: { 'FY2024收入展望': '预计维持中高个位数增长', '资本开支': '约300亿元', '回购计划': '持续回购，全年目标500亿港元' },
            kpi_non_gaap_metrics: { '非IFRS净利润': '1577亿元（剔除投资净损益及股权激励）', '非IFRS净利润率': '25.9%', '调整后EBITDA': '2000亿元' },
        },
        {
            id: 2, report_date: '2024-08-14', fiscal_year: '2024H1', report_type: 'interim',
            closing_date: '2024-08-14', closing_price: 368.2, currency: 'HKD',
            revenue: 323000000000, revenue_yoy_pct: 8.0, gross_profit: 168000000000, gross_margin_pct: 52.0,
            net_profit_parent: 88000000000, adjusted_net_profit_yoy_pct: 53.0,
            net_profit_ex_non_recurring: 82000000000, net_profit_ex_non_recurring_yoy_pct: 48.0,
            net_margin_pct: 27.2, period_expense_ratio_pct: 17.5,
            selling_distribution_expense: 13000000000, admin_expense: 17000000000, finance_cost: 1800000000,
            profit_before_tax: 93000000000, income_tax: 9000000000, ebitda: 108000000000,
            total_assets: 1220000000000, total_liabilities: 560000000000, net_assets_equity: 570000000000,
            debt_to_asset_ratio_pct: 45.9, cash_and_equivalents: 381000000000, interest_bearing_debt: 185000000000,
            current_assets: 540000000000, current_liabilities: 300000000000, current_ratio: 1.80,
            total_equity: 660000000000, parent_equity: 570000000000,
            operating_cash_flow_cfo: 104000000000, investing_cash_flow_cfi: -40000000000,
            financing_cash_flow_cff: -65000000000, free_cash_flow_fcf: 90000000000,
            roe_pct: 31.0, roa_pct: 14.5, eps_basic: 9.42, eps_diluted: 9.18,
            total_dividend: 0, dividend_payout_ratio_pct: 0, dividend_per_share: 0,
            dividend_yield_pct: 0, share_buyback_amount: 28000000000, ex_dividend_date: '',
            pe_ratio: 20.8, pe_ttm: 18.5, pb_ratio: 3.2, ps_ratio: 3.1, ev_ebitda: 13.2, market_cap: 3510000000000,
            revenue_sources: '增值服务(52%)、网络广告(27%)、金融科技及企业服务(21%)',
            revenue_growth_drivers: '微信广告效果提升、视频号商业化加速、游戏出海增长',
            annual_report_major_events: '推出元宝AI助手；混元大模型开源；腾讯云AI算力扩容',
            indicators_step_1_md: `## 中期业绩亮点

**超预期的盈利改善**
- 调整后净利润同比增长53%，远超市场预期
- 毛利率提升至52%，广告业务高毛利贡献提升
- 费用管控成效显著，期间费用率下降0.7ppt`,
            indicators_step_2_md: `## 下半年展望

AI产品商业化（腾讯元宝、Copilot）进入变现阶段，预计贡献增量广告收入。游戏管线（《地下城与勇士：起源》手游）有望在H2带动国内游戏收入加速。维持全年乐观判断。`,
            kpi_metrics: { '微信月活': '13.67亿', '视频号广告收入': '超100亿（半年）', '腾讯云收入': '108亿元', 'AI日活用户': '超500万' },
            kpi_guidance: { 'H2游戏展望': '新游上线，预计同比改善', '广告增长': '预计维持双位数增长', '股票回购': '年内计划总计1000亿港元' },
            kpi_non_gaap_metrics: { '非IFRS净利润': '880亿元', '非IFRS净利润率': '27.2%' },
        },
    ],
}

// ── 比亚迪 01211.HK ─────────────────────────────────────────
const byd: StockDetail = {
    code: 'HK.01211', name: '比亚迪', sector: '新能源汽车', market: 'hk', currency: 'HKD',
    listing_date: '2002-07-31', total_shares: 2904000000,
    klines: genKlines(248, 30, '2025-05-01'),
    reports: [
        { id: 3, title: '比亚迪2024年销量突破427万辆：出海战略与高端化并进', source: '中金公司', publish_date: '2025-02-10', summary: '比亚迪2024年全年销量427.2万辆，同比增长41.3%，稳居全球新能源销量第一。海外销量40.5万辆，同比增长71.9%。仰望、方程豹等高端品牌贡献利润率提升。固态电池研发取得阶段性突破，预计2026年实现装车。目标价360港元。', tags: ['销量', '出海', '高端化', '固态电池'] },
        { id: 4, title: '刀片电池技术壁垒与供应链垂直整合深度分析', source: '华泰证券', publish_date: '2024-11-20', summary: '比亚迪垂直整合率超过75%，在行业降价周期中成本优势持续扩大。刀片电池已向宝马、丰田等主机厂供应，电池业务有望分拆上市。弗迪系列子公司整体估值超千亿，是比亚迪的隐藏资产。', tags: ['电池', '供应链', '弗迪'] },
    ],
    indicators: [
        {
            id: 3, report_date: '2025-03-25', fiscal_year: '2024', report_type: 'annual',
            closing_date: '2025-03-25', closing_price: 295.6, currency: 'HKD',
            revenue: 777100000000, revenue_yoy_pct: 29.0, gross_profit: 148000000000, gross_margin_pct: 19.0,
            net_profit_parent: 35900000000, adjusted_net_profit_yoy_pct: 34.0,
            net_profit_ex_non_recurring: 33000000000, net_profit_ex_non_recurring_yoy_pct: 31.0,
            net_margin_pct: 4.6, period_expense_ratio_pct: 10.2,
            selling_distribution_expense: 42000000000, admin_expense: 18000000000, finance_cost: 2100000000,
            profit_before_tax: 41000000000, income_tax: 7000000000, ebitda: 72000000000,
            total_assets: 680000000000, total_liabilities: 450000000000, net_assets_equity: 178000000000,
            debt_to_asset_ratio_pct: 66.2, cash_and_equivalents: 118000000000, interest_bearing_debt: 95000000000,
            current_assets: 280000000000, current_liabilities: 310000000000, current_ratio: 0.90,
            total_equity: 230000000000, parent_equity: 178000000000,
            operating_cash_flow_cfo: 75000000000, investing_cash_flow_cfi: -110000000000,
            financing_cash_flow_cff: 30000000000, free_cash_flow_fcf: 12000000000,
            roe_pct: 20.2, roa_pct: 5.3, eps_basic: 12.34, eps_diluted: 12.20,
            total_dividend: 8700000000, dividend_payout_ratio_pct: 24.2, dividend_per_share: 3.0,
            dividend_yield_pct: 1.01, share_buyback_amount: 0, ex_dividend_date: '2025-06-10',
            pe_ratio: 23.9, pe_ttm: 22.1, pb_ratio: 4.8, ps_ratio: 1.1, ev_ebitda: 14.2, market_cap: 857000000000,
            revenue_sources: '汽车及相关业务(77%)、手机部件及组装(21%)、二次充电电池及光伏(2%)',
            revenue_growth_drivers: '新能源汽车销量高速增长、海外市场拓展、高端品牌仰望/方程豹放量',
            annual_report_major_events: '发布第五代DM技术（亏电油耗2.9L/100km）；泰国、巴西工厂投产；与宝马合作深化',
            indicators_step_1_md: `## 财务健康度评估

**成长性：卓越**
- 营收同比增长29%，连续4年保持25%+增速
- 净利润增长34%，规模效应逐步显现

**盈利能力：行业领先但仍有压力**
- 毛利率19.0%，处于乘用车行业合理水平
- 净利率4.6%，受研发投入和价格竞争压制

**现金流：稳健**
- 经营现金流750亿元，资本开支1100亿元（扩产期）
- 自由现金流转正，显示盈利质量改善`,
            indicators_step_2_md: `## 投资价值综合判断

**核心逻辑**：比亚迪已建立全球新能源汽车最完整的垂直整合体系，成本护城河宽阔。DM5.0技术将进一步扩大油电同价优势，2025年出海规模预计翻倍。

**关键催化剂**：固态电池商业化、海外建厂降低关税壁垒、高端品牌利润率提升。

**风险**：欧盟关税加征、国内价格战持续、资本开支维持高位压制FCF。`,
            kpi_metrics: { '年度销量': '427.2万辆', '海外销量': '40.5万辆', '纯电销量': '176万辆', 'DM插混销量': '251万辆', '员工总数': '90万人' },
            kpi_guidance: { 'FY2025销量目标': '500万辆以上', '海外目标': '80万辆', '固态电池': '2026年装车计划', '资本开支': '约1200亿元' },
            kpi_non_gaap_metrics: { '扣非净利润': '330亿元', '剔除政府补贴净利润': '约290亿元' },
        },
        {
            id: 4, report_date: '2024-08-28', fiscal_year: '2024H1', report_type: 'interim',
            closing_date: '2024-08-28', closing_price: 228.4, currency: 'HKD',
            revenue: 301100000000, revenue_yoy_pct: 15.8, gross_profit: 54000000000, gross_margin_pct: 18.0,
            net_profit_parent: 13600000000, adjusted_net_profit_yoy_pct: 24.0,
            net_profit_ex_non_recurring: 12500000000, net_profit_ex_non_recurring_yoy_pct: 20.0,
            net_margin_pct: 4.5, period_expense_ratio_pct: 10.5,
            selling_distribution_expense: 17000000000, admin_expense: 8000000000, finance_cost: 900000000,
            profit_before_tax: 15000000000, income_tax: 2500000000, ebitda: 32000000000,
            total_assets: 620000000000, total_liabilities: 420000000000, net_assets_equity: 162000000000,
            debt_to_asset_ratio_pct: 67.7, cash_and_equivalents: 92000000000, interest_bearing_debt: 88000000000,
            current_assets: 250000000000, current_liabilities: 290000000000, current_ratio: 0.86,
            total_equity: 200000000000, parent_equity: 162000000000,
            operating_cash_flow_cfo: 28000000000, investing_cash_flow_cfi: -52000000000,
            financing_cash_flow_cff: 18000000000, free_cash_flow_fcf: -8000000000,
            roe_pct: 16.8, roa_pct: 4.4, eps_basic: 4.68, eps_diluted: 4.61,
            total_dividend: 0, dividend_payout_ratio_pct: 0, dividend_per_share: 0,
            dividend_yield_pct: 0, share_buyback_amount: 0, ex_dividend_date: '',
            pe_ratio: 24.4, pe_ttm: 21.8, pb_ratio: 4.2, ps_ratio: 1.0, ev_ebitda: 13.8, market_cap: 663000000000,
            revenue_sources: '汽车(76%)、手机组装(22%)、电池及光伏(2%)',
            revenue_growth_drivers: '国内新能源渗透率提升、价格战中市占率扩大',
            annual_report_major_events: '荣耀版车型上市带动销量；宣布DM5.0研发完成',
            indicators_step_1_md: `## 中期业绩分析

上半年销量161万辆，同比增长28%。受上半年价格竞争影响，毛利率短暂承压至18%。下半年随DM5.0新车型上市，有望改善盈利结构。`,
            indicators_step_2_md: `## 下半年展望

DM5.0系列车型（秦L、宋L等）下半年全面铺开，预计毛利率回升至19-20%区间。出海步伐加快，泰国工厂Q3投产将降低东南亚市场成本。`,
            kpi_metrics: { '上半年销量': '161万辆', '海外销量': '20.3万辆', '充电站数量': '4000+座' },
            kpi_guidance: { 'H2销量预期': '260万辆以上', '毛利率': '预计回升至19-20%' },
            kpi_non_gaap_metrics: { '扣非净利润': '125亿元' },
        },
    ],
}

// ── 美团 03690.HK ────────────────────────────────────────────
const meituan: StockDetail = {
    code: 'HK.03690', name: '美团-W', sector: '本地生活', market: 'hk', currency: 'HKD',
    listing_date: '2018-09-20', total_shares: 6130000000,
    klines: genKlines(148, 30, '2025-05-01'),
    reports: [
        { id: 5, title: '美团2024年报：即时配送护城河深化，出海战略初见成效', source: '花旗银行', publish_date: '2025-03-20', summary: '美团2024年营收同比增长22%至3376亿元。外卖业务订单量增长15%，即时零售（闪购）成为第二增长极，GTV突破1200亿元。国际业务Keeta在沙特、香港持续扩张，日订单量超百万。维持买入，目标价175港元。', tags: ['外卖', '即时零售', '出海', 'Keeta'] },
        { id: 6, title: '本地生活格局再梳理：美团 vs 抖音本地生活竞争终局研判', source: '招商证券', publish_date: '2024-10-08', summary: '抖音本地生活到店业务GTV已达约3000亿元，对美团到店业务形成实质压力。但外卖护城河（配送网络+运力+用户习惯）短期难以撼动。美团通过"神会员"提升用户粘性，预计到店业务增速短期承压但份额基本稳定。', tags: ['竞争', '到店', '抖音', '外卖'] },
    ],
    indicators: [
        {
            id: 5, report_date: '2025-03-20', fiscal_year: '2024', report_type: 'annual',
            closing_date: '2025-03-20', closing_price: 158.3, currency: 'HKD',
            revenue: 337600000000, revenue_yoy_pct: 22.0, gross_profit: 123000000000, gross_margin_pct: 36.5,
            net_profit_parent: 28000000000, adjusted_net_profit_yoy_pct: 145.0,
            net_profit_ex_non_recurring: 26000000000, net_profit_ex_non_recurring_yoy_pct: 130.0,
            net_margin_pct: 8.3, period_expense_ratio_pct: 24.1,
            selling_distribution_expense: 58000000000, admin_expense: 12000000000, finance_cost: 800000000,
            profit_before_tax: 32000000000, income_tax: 5000000000, ebitda: 48000000000,
            total_assets: 310000000000, total_liabilities: 195000000000, net_assets_equity: 105000000000,
            debt_to_asset_ratio_pct: 62.9, cash_and_equivalents: 98000000000, interest_bearing_debt: 35000000000,
            current_assets: 145000000000, current_liabilities: 125000000000, current_ratio: 1.16,
            total_equity: 115000000000, parent_equity: 105000000000,
            operating_cash_flow_cfo: 40000000000, investing_cash_flow_cfi: -18000000000,
            financing_cash_flow_cff: -8000000000, free_cash_flow_fcf: 35000000000,
            roe_pct: 26.7, roa_pct: 9.0, eps_basic: 4.57, eps_diluted: 4.48,
            total_dividend: 0, dividend_payout_ratio_pct: 0, dividend_per_share: 0,
            dividend_yield_pct: 0, share_buyback_amount: 12000000000, ex_dividend_date: '',
            pe_ratio: 34.6, pe_ttm: 30.2, pb_ratio: 9.2, ps_ratio: 2.8, ev_ebitda: 18.4, market_cap: 970000000000,
            revenue_sources: '核心本地商业(89%)、新业务(11%)',
            revenue_growth_drivers: '外卖订单量增长、即时零售品类扩张、到店酒旅复苏',
            annual_report_major_events: 'Keeta进入沙特阿拉伯；推出"神会员"超级会员体系；美团无人机配送商业化运营',
            indicators_step_1_md: `## 财务健康度评估

**盈利能力：大幅改善**
- 净利润同比增长145%，盈利拐点确立
- 核心本地商业经营利润率提升至12%
- 自由现金流350亿元，现金积累加速

**成长质量：优**
- 即时零售（闪购）GMV超1200亿，成为重要增量
- 新业务亏损收窄，拖累减少`,
            indicators_step_2_md: `## 投资价值综合判断

美团完成从"增长优先"到"盈利优先"的战略转型。外卖业务护城河（5亿+骑手网络、15年运营数据积累）短期内竞争者难以复制。国际业务Keeta若能在中东形成规模，将打开新的估值空间。

当前PE约30x，相对平台类公司合理，考虑到利润高速增长，PEG约0.6x，具吸引力。`,
            kpi_metrics: { '外卖日均订单': '8500万单', '即时零售GTV': '超1200亿元', 'Keeta日订单': '100万单+', '骑手数量': '750万', '平台商家数': '1000万+' },
            kpi_guidance: { 'FY2025收入': '预计增长15-20%', '国际业务': 'Keeta计划进入5个新市场', '利润率': '核心业务利润率维持12-14%' },
            kpi_non_gaap_metrics: { '经调整净利润': '280亿元（剔除股权激励及投资损益）', '核心本地商业经营利润': '450亿元' },
        },
    ],
}

// ── 中国平安 02318.HK ────────────────────────────────────────
const pingan: StockDetail = {
    code: 'HK.02318', name: '中国平安', sector: '保险金融', market: 'hk', currency: 'HKD',
    listing_date: '2004-06-24', total_shares: 18280000000,
    klines: genKlines(42, 30, '2025-05-01'),
    reports: [
        { id: 7, title: '中国平安2024年报：寿险改革成效显现，NBV增速超预期', source: '瑞银', publish_date: '2025-03-15', summary: '平安2024年新业务价值（NBV）同比增长11%至392亿元，代理人质态持续改善。综合金融模式协同效应增强，银行业务稳健。科技板块贡献利润占比提升至10%。每股股息1.5元，股息率约5.5%。维持买入，目标价58港元。', tags: ['保险', 'NBV', '股息', '综合金融'] },
        { id: 8, title: '平安医疗生态圈：从健康管理到慢病服务的商业化路径', source: '国泰君安', publish_date: '2024-09-12', summary: '平安好医生月活用户超4000万，慢病管理服务续约率达85%。医疗生态圈已覆盖超1亿用户，成为寿险销售的重要差异化工具。预计2025年医疗科技板块贡献净利润约50亿元。', tags: ['医疗', '科技', '平安好医生'] },
    ],
    indicators: [
        {
            id: 6, report_date: '2025-03-14', fiscal_year: '2024', report_type: 'annual',
            closing_date: '2025-03-14', closing_price: 48.5, currency: 'HKD',
            revenue: 912000000000, revenue_yoy_pct: 6.2, gross_profit: 180000000000, gross_margin_pct: 19.7,
            net_profit_parent: 126800000000, adjusted_net_profit_yoy_pct: 6.8,
            net_profit_ex_non_recurring: 120000000000, net_profit_ex_non_recurring_yoy_pct: 5.5,
            net_margin_pct: 13.9, period_expense_ratio_pct: 0,
            selling_distribution_expense: 95000000000, admin_expense: 22000000000, finance_cost: 0,
            profit_before_tax: 148000000000, income_tax: 22000000000, ebitda: 0,
            total_assets: 12800000000000, total_liabilities: 11900000000000, net_assets_equity: 780000000000,
            debt_to_asset_ratio_pct: 93.0, cash_and_equivalents: 280000000000, interest_bearing_debt: 0,
            current_assets: 0, current_liabilities: 0, current_ratio: 0,
            total_equity: 900000000000, parent_equity: 780000000000,
            operating_cash_flow_cfo: 320000000000, investing_cash_flow_cfi: -250000000000,
            financing_cash_flow_cff: -80000000000, free_cash_flow_fcf: 0,
            roe_pct: 16.3, roa_pct: 1.0, eps_basic: 6.94, eps_diluted: 6.92,
            total_dividend: 27400000000, dividend_payout_ratio_pct: 21.6, dividend_per_share: 1.5,
            dividend_yield_pct: 5.5, share_buyback_amount: 5000000000, ex_dividend_date: '2025-07-05',
            pe_ratio: 7.0, pe_ttm: 6.8, pb_ratio: 1.14, ps_ratio: 0.95, ev_ebitda: 0, market_cap: 886000000000,
            revenue_sources: '寿健险(68%)、财产险(18%)、银行(10%)、科技(4%)',
            revenue_growth_drivers: '寿险代理人质改革推进、NBV增长超预期、综合金融交叉销售',
            annual_report_major_events: '寿险改革第三年成效验证；平安银行不良率改善；宣布回购100亿元',
            indicators_step_1_md: `## 财务健康度评估

**盈利稳健，分红吸引力高**
- 归母净利润1268亿元，同比增长6.8%
- 每股股息1.5元，股息率约5.5%，防御性强
- NBV增长11%，领先行业平均

**特殊性说明**（保险公司）
- 资产负债率93%为行业惯例（主要为保险负债）
- 应关注综合偿付能力充足率（201%，行业优秀）`,
            indicators_step_2_md: `## 投资价值综合判断

平安当前PB约1.14x，处历史低位区间。寿险改革已进入收获期，代理人人均产能提升明显。5.5%股息率在低利率环境下具备显著吸引力。

科技板块（陆金所、平安好医生、金融壹账通）的估值修复是潜在催化剂。`,
            kpi_metrics: { '寿健险代理人': '36.2万人', 'NBV': '392亿元', '综合偿付能力充足率': '201%', '平安好医生月活': '4000万', '银行不良贷款率': '1.06%' },
            kpi_guidance: { 'FY2025 NBV': '预计增长10-15%', '股息政策': '维持每年增长', '科技业务': '争取IPO窗口' },
            kpi_non_gaap_metrics: { '营运利润': '1400亿元（剔除短期投资波动）', '寿健险营运ROE': '16.5%' },
        },
    ],
}

// ── 阿里巴巴 09988.HK ────────────────────────────────────────
const alibaba: StockDetail = {
    code: 'HK.09988', name: '阿里巴巴-W', sector: '电商科技', market: 'hk', currency: 'HKD',
    listing_date: '2019-11-26', total_shares: 21720000000,
    klines: genKlines(98, 30, '2025-05-01'),
    reports: [
        { id: 9, title: '阿里巴巴FY2025年报：云业务AI驱动加速，电商基本盘企稳', source: '摩根士丹利', publish_date: '2025-05-16', summary: '阿里FY2025全年收入同比增长7%至10120亿元。云智能集团收入同比增长13%，AI相关产品收入连续六个季度三位数增长。淘天集团GMV增速转正，88VIP会员突破4500万。调整后净利润同比增长7%至1647亿元。目标价135港元。', tags: ['云计算', 'AI', '电商', '88VIP'] },
        { id: 10, title: '阿里云通义千问：大模型商业化路径与市场份额分析', source: '高盛', publish_date: '2025-02-28', summary: '通义千问Qwen2.5系列在开源模型中位居前列，API调用量同比增长超10倍。阿里云AI基础设施客户数超10万，企业级AI应用已覆盖金融、零售、制造三大行业。预计FY2026云业务AI贡献收入超200亿元。', tags: ['通义千问', 'AI', '云计算', '大模型'] },
    ],
    indicators: [
        {
            id: 7, report_date: '2025-05-15', fiscal_year: 'FY2025', report_type: 'annual',
            closing_date: '2025-05-15', closing_price: 112.4, currency: 'HKD',
            revenue: 1012000000000, revenue_yoy_pct: 7.0, gross_profit: 492000000000, gross_margin_pct: 48.6,
            net_profit_parent: 144000000000, adjusted_net_profit_yoy_pct: 7.0,
            net_profit_ex_non_recurring: 138000000000, net_profit_ex_non_recurring_yoy_pct: 5.5,
            net_margin_pct: 14.2, period_expense_ratio_pct: 28.0,
            selling_distribution_expense: 120000000000, admin_expense: 55000000000, finance_cost: 3000000000,
            profit_before_tax: 162000000000, income_tax: 22000000000, ebitda: 220000000000,
            total_assets: 1380000000000, total_liabilities: 580000000000, net_assets_equity: 720000000000,
            debt_to_asset_ratio_pct: 42.0, cash_and_equivalents: 429000000000, interest_bearing_debt: 88000000000,
            current_assets: 620000000000, current_liabilities: 320000000000, current_ratio: 1.94,
            total_equity: 800000000000, parent_equity: 720000000000,
            operating_cash_flow_cfo: 218000000000, investing_cash_flow_cfi: -95000000000,
            financing_cash_flow_cff: -145000000000, free_cash_flow_fcf: 185000000000,
            roe_pct: 20.0, roa_pct: 10.4, eps_basic: 6.63, eps_diluted: 6.51,
            total_dividend: 0, dividend_payout_ratio_pct: 0, dividend_per_share: 0,
            dividend_yield_pct: 0, share_buyback_amount: 85000000000, ex_dividend_date: '',
            pe_ratio: 16.9, pe_ttm: 15.8, pb_ratio: 2.3, ps_ratio: 2.4, ev_ebitda: 9.8, market_cap: 2440000000000,
            revenue_sources: '淘天集团(52%)、云智能(10%)、国际商业(10%)、菜鸟(8%)、本地生活(8%)、大文娱(3%)、其他(9%)',
            revenue_growth_drivers: 'AI拉动云业务加速、88VIP会员增长带动淘天货币化率提升、国际电商（速卖通/Lazada）增长',
            annual_report_major_events: '宣布组织架构调整（六大集团）；通义千问Qwen3发布；回购金额创历史新高',
            indicators_step_1_md: `## 财务健康度评估

**现金储备雄厚，回购力度大**
- 货币资金4290亿元，处于净现金状态
- 年内回购850亿元，每股价值持续提升
- 自由现金流1850亿元，资金实力极强

**盈利质量稳健**
- 毛利率48.6%，平台属性带来高毛利
- 云业务AI驱动进入加速通道`,
            indicators_step_2_md: `## 投资价值综合判断

阿里当前PEG（考虑回购后每股收益增速）约0.8x，处于历史低估区间。云AI业务的三位数增长有望在FY2026带动整体估值重塑。

核心风险：淘天GMV增速能否持续转正、国际电商竞争（Temu、TikTok Shop）加剧、监管不确定性。`,
            kpi_metrics: { '淘天GMV': '约8万亿元', '88VIP会员': '4500万', '云AI收入增速': '连续6季度三位数增长', '日均包裹量': '1.6亿个（菜鸟）', '速卖通买家数': '3.5亿' },
            kpi_guidance: { 'FY2026云收入': '预计增长15-20%', '资本开支': '约380亿元（重点AI算力）', '回购计划': 'FY2026继续回购' },
            kpi_non_gaap_metrics: { '调整后EBITA': '1647亿元', '调整后净利润率': '16.3%', '云业务调整后EBITA利润率': '9.8%' },
        },
    ],
}

// ── 导出列表数据 ─────────────────────────────────────────────
export const mockStocks: StockDetail[] = [tencent, byd, meituan, pingan, alibaba]

export const mockStockList: StockListItem[] = mockStocks.map(s => {
    const latest = s.indicators[0]
    return {
        code: s.code, name: s.name, sector: s.sector,
        market: s.market, currency: s.currency,
        listing_date: s.listing_date, total_shares: s.total_shares,
        market_cap: latest.market_cap,
        pe_ttm: latest.pe_ttm,
        pb_ratio: latest.pb_ratio,
        revenue_yoy_pct: latest.revenue_yoy_pct,
        net_profit_yoy_pct: latest.adjusted_net_profit_yoy_pct,
        latest_report_date: latest.report_date,
        closing_price: latest.closing_price,
    }
})
